import { useEffect } from "react";

function scrollToElement(target: HTMLElement) {
  const viewportHeight = window.visualViewport?.height;
  if (!viewportHeight) {
    return;
  }

  const rect = target.getBoundingClientRect();
  const isInViewport = rect.top >= 0 && rect.bottom <= viewportHeight;
  if (isInViewport) {
    return;
  }

  let scrollPosition = rect.top;

  // For large elements (like big textareas), we want to focus on where the cursor is
  if (
    target.tagName === "TEXTAREA" ||
    target.getAttribute("contenteditable") === "true"
  ) {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const cursorRect = range.getBoundingClientRect();
      scrollPosition = document.body.scrollTop + cursorRect.top;
    }
  }

  const viewportIsInView =
    scrollPosition >= document.body.scrollTop &&
    scrollPosition <= document.body.scrollTop + viewportHeight;
  if (viewportIsInView) {
    return;
  }

  // scroll to the center of the viewport
  scrollPosition -= viewportHeight / 2;

  document.body.scrollTo({
    top: scrollPosition,
    behavior: "smooth",
  });
}

/**
 * Utility hook that ensures focused input elements are visible in the viewport
 * Works with input, textarea, and contenteditable elements
 * Note: This hook assumes that the body element is scrollable (not html)
 */
export function useViewportElementScroll() {
  useEffect(() => {
    let scheduledScrollTarget: HTMLElement | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const visualViewport = window.visualViewport;
    if (!visualViewport) {
      return;
    }

    const scrollToTargetIfScheduled = () => {
      const target = scheduledScrollTarget;
      scheduledScrollTarget = undefined;

      clearTimeout(timeout);
      timeout = undefined;

      if (target) {
        scrollToElement(target);
      }
    };

    const handleFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement;

      const isInputElement =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.getAttribute("contenteditable") === "true";
      if (!isInputElement) {
        return;
      }

      scheduledScrollTarget = target;
      timeout = setTimeout(scrollToTargetIfScheduled, 2000);
    };

    // Listen for focus events on the document
    document.addEventListener("focus", handleFocus, true);
    visualViewport.addEventListener("resize", scrollToTargetIfScheduled);

    return () => {
      document.removeEventListener("focus", handleFocus, true);
      visualViewport.removeEventListener("resize", scrollToTargetIfScheduled);
      clearTimeout(timeout);
    };
  }, []);
}
